import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BadgeCelebration } from '../../../components/BadgeCelebration';
import { DevModeSwitcher } from '../../../components/DevModeSwitcher';
import { PortalFooter } from './PortalFooter';
import { MainButton } from './aeroglass/MainButton';
import { Background } from './aeroglass/Background';
import { MenuArc } from './aeroglass/MenuArc';
import { ExpandedSearch } from '../../../components/ExpandedSearch';
import { CreateOptionsOverlay } from './CreateOptionsOverlay';
import { GlassDrawer } from './aeroglass/GlassDrawer';

interface PublicLayoutProps {
  children: React.ReactNode;
}

export const PublicLayout: React.FC<PublicLayoutProps> = ({ children }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const navigate = useNavigate();

  // First-time visit detection
  useEffect(() => {
    const hasSeenOnboarding = localStorage.getItem('hasSeenOnboarding');
    if (!hasSeenOnboarding) {
        navigate('/boas-vindas');
    }
  }, [navigate]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleMenuClick = (id: string) => {
    // 1. Close the Arc Menu immediately for better feedback
    setIsMenuOpen(false);

    // 2. Handle Action
    switch (id) {
      case 'profile':
        navigate('/admin/profile'); 
        break;
      case 'community':
        navigate('/explorar');
        break;
      case 'create':
        setIsCreateOpen(true);
        break;
      case 'notifications':
        navigate('/notificacoes');
        break;
      case 'ai':
        setIsSearchOpen(true);
        break;
      case 'menu':
        setIsDrawerOpen(true);
        break;
    }
  };
  
  const handleNavigation = (direction: 'up' | 'down' | 'left' | 'right') => {
    console.log('Magic Button Navigation:', direction);
    
    // Magic Button Gestures Implementation
    switch (direction) {
        case 'up':
            // Scroll Down (Drag Up = Content moves Up... wait, "Swipe Up" usually means "Move Viewport Down" or "Move Content Up")
            // Convention: Swipe Up -> Scroll Down
            window.scrollBy({ top: 300, behavior: 'smooth' });
            break;
        case 'down':
            // Scroll Up (Swipe Down -> Scroll Up or Refresh)
            window.scrollBy({ top: -300, behavior: 'smooth' });
            break;
        case 'left':
            // Navigate Left (Back or Previous Tab)
            navigate(-1);
            break;
        case 'right':
            // Navigate Right (Forward or Next Tab)
            navigate(1); // or specific route
            break;
    }
  };

  return (
    <div className="min-h-screen bg-[#0F172A] text-white flex flex-col font-sans relative overflow-hidden">
      
      {/* Aeroglass Background */}
      <Background isMenuOpen={isMenuOpen} />

      {/* Main Content Area - Scale Effect */}
      <main className={`flex-grow relative z-10 transition-all duration-500 origin-bottom ${
        isMenuOpen || isDrawerOpen || isSearchOpen || isCreateOpen ? 'scale-[0.92] opacity-80 blur-[2px]' : 'scale-100 opacity-100 blur-0'
      }`}>
        <div className="min-h-screen flex flex-col">
          {children}
          <PortalFooter />
        </div>
      </main>

      {/* Footer Area with Controls Overlay */}
      <div className={`fixed bottom-0 left-0 w-full flex justify-center pb-[30px] pt-32 bg-gradient-to-t from-[#0F172A] via-[#0F172A]/80 to-transparent pointer-events-none z-50 transition-opacity duration-300 ${isDrawerOpen || isSearchOpen || isCreateOpen ? 'opacity-0 scale-90 pointer-events-none' : 'opacity-100'}`}>
         {/* The Arc Menu Items */}
         <MenuArc isOpen={isMenuOpen} onItemClick={handleMenuClick} />

         {/* The Main Interaction Button */}
         <div className="pointer-events-auto relative z-50">
             <MainButton 
                isOpen={isMenuOpen} 
                onClick={toggleMenu} 
                onNavigate={handleNavigation}
             />
         </div>
      </div>

      {/* Overlays */}
      <GlassDrawer isOpen={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} />
      <ExpandedSearch isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
      <CreateOptionsOverlay isOpen={isCreateOpen} onClose={() => setIsCreateOpen(false)} />

      {/* Componentes flutuantes auxiliares */}
      <BadgeCelebration />
      <DevModeSwitcher />
    </div>
  );
};
